import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { LoadingSpinner, SkeletonBlock } from '../../../components/SkeletonLoader';

const TrafficMap = () => {
  const [selectedLayer, setSelectedLayer] = useState('traffic');
  const [expandedIncident, setExpandedIncident] = useState(null);
  const [isMapLoading, setIsMapLoading] = useState(true);

  useEffect(() => {
    const delay = 800 + Math.random() * 400; // 800–1200ms
    const timer = setTimeout(() => setIsMapLoading(false), delay);
    return () => clearTimeout(timer);
  }, []);

  const mapLayers = [
    { id: 'traffic', label: 'Trafikk', icon: 'Car', color: '#004F98' },
    { id: 'construction', label: 'Anleggsarbeid', icon: 'HardHat', color: '#FF5722' },
    { id: 'weather', label: 'Vær', icon: 'Cloud', color: '#6B7280' },
  ];

  const allIncidents = {
    traffic: [
      {
        id: 1,
        type: 'heavy',
        location: 'E6 Oslo – Gardermoen',
        severity: 'high',
        description: 'Tung trafikk, forsinkelse 15–20 min',
        time: '08:42',
        badge: 'Kø',
      },
      {
        id: 2,
        type: 'accident',
        location: 'Ring 3 Ullevål',
        severity: 'high',
        description: 'Ulykke, vei stengt. Bruk omkjøring via Tåsen.',
        time: '09:15',
        badge: 'Ulykke',
      },
      {
        id: 3,
        type: 'slow',
        location: 'E18 Lysaker – Sandvika',
        severity: 'medium',
        description: 'Saktegående trafikk, ca. 10 min forsinkelse',
        time: '09:30',
        badge: 'Sakte',
      },
    ],
    construction: [
      {
        id: 4,
        type: 'construction',
        location: 'E18 Asker',
        severity: 'medium',
        description: 'Veiarbeid, ett felt stengt. Forventet ferdig 15. mars.',
        time: '07:00',
        badge: 'Arbeid',
      },
      {
        id: 5,
        type: 'construction',
        location: 'Rv4 Kjul – Rotnes',
        severity: 'low',
        description: 'Gravearbeid langs veikant, redusert hastighet 50 km/t',
        time: '06:30',
        badge: 'Arbeid',
      },
    ],
    weather: [
      {
        id: 6,
        type: 'weather',
        location: 'E16 Filefjell',
        severity: 'high',
        description: 'Snøfall og glatt vei. Kjetting påbudt for tunge kjøretøy.',
        time: '10:00',
        badge: 'Glatt',
      },
      {
        id: 7,
        type: 'weather',
        location: 'Rv7 Hardangervidda',
        severity: 'medium',
        description: 'Sterk vind og redusert sikt. Kolonnekjøring.',
        time: '09:45',
        badge: 'Vind',
      },
    ],
  };

  const trafficIncidents = allIncidents?.[selectedLayer] || [];

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'high': return '#EF4444';
      case 'medium': return '#F59E0B';
      default: return '#10B981';
    }
  };

  const getSeverityBg = (severity) => {
    switch (severity) {
      case 'high': return 'rgba(239,68,68,0.12)';
      case 'medium': return 'rgba(245,158,11,0.12)';
      default: return 'rgba(16,185,129,0.12)';
    }
  };

  const getIncidentIcon = (type) => {
    switch (type) {
      case 'heavy': return 'AlertTriangle';
      case 'construction': return 'HardHat';
      case 'accident': return 'AlertCircle';
      case 'slow': return 'TrendingDown';
      case 'weather': return 'CloudSnow';
      default: return 'Info';
    }
  };

  const activeLayer = mapLayers?.find(l => l?.id === selectedLayer);

  return (
    <div className="relative w-full h-full bg-card border-2 border-border rounded-[var(--radius-md)] overflow-hidden">
      {/* Map Loading Skeleton */}
      {isMapLoading && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            zIndex: 20,
            background: 'var(--color-card)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0',
          }}
        >
          {/* Skeleton map grid */}
          <div style={{ position: 'absolute', inset: 0, overflow: 'hidden', opacity: 0.5 }}>
            {/* Horizontal road lines */}
            {[15, 30, 45, 60, 75]?.map(top => (
              <div
                key={top}
                style={{
                  position: 'absolute',
                  top: `${top}%`,
                  left: 0,
                  right: 0,
                  height: '2px',
                  background: 'linear-gradient(90deg, rgba(255,255,255,0.04) 25%, rgba(255,255,255,0.1) 50%, rgba(255,255,255,0.04) 75%)',
                  backgroundSize: '200% 100%',
                  animation: 'skeletonPulse 1.6s ease-in-out infinite',
                  animationDelay: `${top * 0.02}s`,
                }}
              />
            ))}
            {/* Vertical road lines */}
            {[20, 40, 60, 80]?.map(left => (
              <div
                key={left}
                style={{
                  position: 'absolute',
                  left: `${left}%`,
                  top: 0,
                  bottom: 0,
                  width: '2px',
                  background: 'linear-gradient(180deg, rgba(255,255,255,0.04) 25%, rgba(255,255,255,0.1) 50%, rgba(255,255,255,0.04) 75%)',
                  backgroundSize: '100% 200%',
                  animation: 'skeletonPulse 1.6s ease-in-out infinite',
                  animationDelay: `${left * 0.015}s`,
                }}
              />
            ))}
            {/* Block patches */}
            {[
              { top: '10%', left: '15%', w: '18%', h: '12%' },
              { top: '35%', left: '50%', w: '22%', h: '10%' },
              { top: '55%', left: '25%', w: '16%', h: '14%' },
              { top: '20%', left: '70%', w: '14%', h: '18%' },
              { top: '65%', left: '65%', w: '20%', h: '12%' },
            ]?.map((block, i) => (
              <div
                key={i}
                style={{
                  position: 'absolute',
                  top: block?.top,
                  left: block?.left,
                  width: block?.w,
                  height: block?.h,
                  background: 'linear-gradient(90deg, rgba(255,255,255,0.03) 25%, rgba(255,255,255,0.07) 50%, rgba(255,255,255,0.03) 75%)',
                  backgroundSize: '200% 100%',
                  animation: 'skeletonPulse 1.6s ease-in-out infinite',
                  animationDelay: `${i * 0.2}s`,
                  borderRadius: '2px',
                }}
              />
            ))}
          </div>
          {/* Center spinner + label */}
          <div style={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '14px' }}>
            <div
              style={{
                width: '56px',
                height: '56px',
                borderRadius: '50%',
                background: 'rgba(255,87,34,0.1)',
                border: '2px solid rgba(255,87,34,0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <LoadingSpinner size={32} color="#FF5722" />
            </div>
            <span
              style={{
                fontFamily: 'var(--font-heading)',
                fontSize: '12px',
                fontWeight: 700,
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                color: 'var(--color-muted-foreground)',
              }}
            >
              Laster kart...
            </span>
          </div>
          {/* Layer toggle skeleton */}
          <div
            style={{
              position: 'absolute',
              top: '16px',
              left: '16px',
              display: 'flex',
              gap: '8px',
            }}
          >
            {[80, 110, 70]?.map((w, i) => (
              <div
                key={i}
                style={{
                  width: `${w}px`,
                  height: '34px',
                  borderRadius: 'var(--radius-sm)',
                  background: 'linear-gradient(90deg, rgba(255,255,255,0.04) 25%, rgba(255,255,255,0.1) 50%, rgba(255,255,255,0.04) 75%)',
                  backgroundSize: '200% 100%',
                  animation: 'skeletonPulse 1.6s ease-in-out infinite',
                  animationDelay: `${i * 0.15}s`,
                  border: '2px solid var(--color-border)',
                }}
              />
            ))}
          </div>
          {/* Incidents panel skeleton */}
          <div
            style={{
              position: 'absolute',
              bottom: '16px',
              right: '16px',
              width: 'min(340px, calc(100% - 32px))',
              background: 'var(--color-card)',
              border: '2px solid var(--color-border)',
              borderRadius: 'var(--radius-md)',
              overflow: 'hidden',
            }}
          >
            <div style={{ padding: '12px 16px', borderBottom: '2px solid var(--color-border)', background: 'var(--color-muted)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <SkeletonBlock width="130px" height="14px" />
              <SkeletonBlock width="60px" height="22px" style={{ borderRadius: 'var(--radius-sm)' }} />
            </div>
            <div style={{ padding: '8px' }}>
              {[0, 1, 2]?.map(i => (
                <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', padding: '10px 12px', marginBottom: i < 2 ? '4px' : 0 }}>
                  <SkeletonBlock width="36px" height="36px" style={{ borderRadius: '8px', flexShrink: 0 }} />
                  <div style={{ flex: 1 }}>
                    <SkeletonBlock width="120px" height="13px" style={{ marginBottom: '6px' }} />
                    <SkeletonBlock width="160px" height="11px" />
                  </div>
                  <SkeletonBlock width="30px" height="11px" style={{ flexShrink: 0 }} />
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      {/* Layer Toggle Bar */}
      <div className="absolute top-4 left-4 z-10 flex gap-2">
        {mapLayers?.map((layer) => {
          const isActive = selectedLayer === layer?.id;
          return (
            <button
              key={layer?.id}
              onClick={() => { setSelectedLayer(layer?.id); setExpandedIncident(null); }}
              aria-label={`Vis ${layer?.label}`}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '7px 14px',
                borderRadius: 'var(--radius-sm)',
                border: `2px solid ${isActive ? layer?.color : 'var(--color-border)'}`,
                background: isActive ? layer?.color : 'var(--color-card)',
                color: isActive ? '#fff' : 'var(--color-muted-foreground)',
                fontFamily: 'var(--font-heading)',
                fontSize: '12px',
                fontWeight: 600,
                letterSpacing: '0.04em',
                cursor: 'pointer',
                transition: 'all 180ms ease',
                boxShadow: isActive ? `0 2px 12px ${layer?.color}40` : '0 1px 4px rgba(0,0,0,0.12)',
                whiteSpace: 'nowrap',
              }}
            >
              <Icon name={layer?.icon} size={15} color={isActive ? '#fff' : layer?.color} />
              <span className="hidden sm:inline">{layer?.label}</span>
              {isActive && (
                <span style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '18px',
                  height: '18px',
                  borderRadius: '50%',
                  background: 'rgba(255,255,255,0.25)',
                  fontSize: '10px',
                  fontWeight: 700,
                  color: '#fff',
                  marginLeft: '2px',
                }}>
                  {trafficIncidents?.length}
                </span>
              )}
            </button>
          );
        })}
      </div>
      {/* Map iframe */}
      <div className="w-full h-full">
        <iframe
          width="100%"
          height="100%"
          loading="lazy"
          title="Norwegian Road Network Map"
          referrerPolicy="no-referrer-when-downgrade"
          src="https://www.google.com/maps?q=59.9139,10.7522&z=10&output=embed"
          className="w-full h-full"
          style={{ filter: 'invert(90%) hue-rotate(180deg) saturate(0.8)' }}
        />
      </div>
      {/* Aktive hendelser Panel */}
      <div
        className="absolute bottom-4 right-4 z-10"
        style={{ width: 'min(340px, calc(100% - 32px))' }}
      >
        <div
          style={{
            background: 'var(--color-card)',
            border: '2px solid var(--color-border)',
            borderRadius: 'var(--radius-md)',
            overflow: 'hidden',
            boxShadow: '0 8px 32px rgba(0,0,0,0.22)',
          }}
        >
          {/* Panel Header */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '12px 16px',
              borderBottom: '2px solid var(--color-border)',
              background: 'var(--color-muted)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div
                style={{
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  background: activeLayer?.color || 'var(--color-primary)',
                  boxShadow: `0 0 0 3px ${activeLayer?.color || 'var(--color-primary)'}30`,
                  animation: 'pulse 2s infinite',
                }}
              />
              <span
                style={{
                  fontFamily: 'var(--font-heading)',
                  fontSize: '13px',
                  fontWeight: 700,
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                  color: 'var(--color-foreground)',
                }}
              >
                Aktive hendelser
              </span>
            </div>
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
                padding: '3px 10px',
                borderRadius: 'var(--radius-sm)',
                background: `${activeLayer?.color || 'var(--color-primary)'}18`,
                border: `1.5px solid ${activeLayer?.color || 'var(--color-primary)'}40`,
                fontFamily: 'var(--font-heading)',
                fontSize: '11px',
                fontWeight: 700,
                color: activeLayer?.color || 'var(--color-primary)',
              }}
            >
              {trafficIncidents?.length} varsler
            </span>
          </div>

          {/* Incident List */}
          <div
            style={{
              maxHeight: '240px',
              overflowY: 'auto',
              padding: '8px',
            }}
            className="scrollbar-custom"
          >
            {trafficIncidents?.length === 0 ? (
              <div
                style={{
                  padding: '24px 16px',
                  textAlign: 'center',
                  color: 'var(--color-muted-foreground)',
                  fontFamily: 'var(--font-caption)',
                  fontSize: '13px',
                }}
              >
                <Icon name="CheckCircle" size={28} color="var(--color-success)" style={{ marginBottom: '8px' }} />
                <div>Ingen aktive hendelser</div>
              </div>
            ) : (
              trafficIncidents?.map((incident, idx) => (
                <button
                  key={incident?.id}
                  onClick={() => setExpandedIncident(expandedIncident === incident?.id ? null : incident?.id)}
                  style={{
                    display: 'block',
                    width: '100%',
                    textAlign: 'left',
                    padding: '10px 12px',
                    borderRadius: 'var(--radius-sm)',
                    border: `1.5px solid ${expandedIncident === incident?.id ? getSeverityColor(incident?.severity) : 'transparent'}`,
                    background: expandedIncident === incident?.id ? getSeverityBg(incident?.severity) : 'transparent',
                    cursor: 'pointer',
                    transition: 'all 150ms ease',
                    marginBottom: idx < trafficIncidents?.length - 1 ? '4px' : '0',
                  }}
                  onMouseEnter={e => {
                    if (expandedIncident !== incident?.id) {
                      e.currentTarget.style.background = 'var(--color-muted)';
                      e.currentTarget.style.borderColor = 'var(--color-border)';
                    }
                  }}
                  onMouseLeave={e => {
                    if (expandedIncident !== incident?.id) {
                      e.currentTarget.style.background = 'transparent';
                      e.currentTarget.style.borderColor = 'transparent';
                    }
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                    {/* Severity dot + icon */}
                    <div
                      style={{
                        flexShrink: 0,
                        width: '36px',
                        height: '36px',
                        borderRadius: '8px',
                        background: getSeverityBg(incident?.severity),
                        border: `1.5px solid ${getSeverityColor(incident?.severity)}40`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Icon name={getIncidentIcon(incident?.type)} size={18} color={getSeverityColor(incident?.severity)} />
                    </div>

                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '2px' }}>
                        <span
                          style={{
                            fontFamily: 'var(--font-heading)',
                            fontSize: '12px',
                            fontWeight: 600,
                            color: 'var(--color-foreground)',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            flex: 1,
                          }}
                        >
                          {incident?.location}
                        </span>
                        <span
                          style={{
                            flexShrink: 0,
                            padding: '1px 6px',
                            borderRadius: '4px',
                            background: getSeverityBg(incident?.severity),
                            border: `1px solid ${getSeverityColor(incident?.severity)}50`,
                            fontFamily: 'var(--font-heading)',
                            fontSize: '10px',
                            fontWeight: 700,
                            color: getSeverityColor(incident?.severity),
                            letterSpacing: '0.04em',
                          }}
                        >
                          {incident?.badge}
                        </span>
                      </div>

                      {expandedIncident === incident?.id ? (
                        <p
                          style={{
                            fontFamily: 'var(--font-caption)',
                            fontSize: '12px',
                            color: 'var(--color-muted-foreground)',
                            lineHeight: 1.5,
                            margin: 0,
                          }}
                        >
                          {incident?.description}
                        </p>
                      ) : (
                        <p
                          style={{
                            fontFamily: 'var(--font-caption)',
                            fontSize: '12px',
                            color: 'var(--color-muted-foreground)',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            margin: 0,
                          }}
                        >
                          {incident?.description}
                        </p>
                      )}
                    </div>

                    <span
                      style={{
                        flexShrink: 0,
                        fontFamily: 'var(--font-caption)',
                        fontSize: '10px',
                        color: 'var(--color-muted-foreground)',
                        paddingTop: '2px',
                      }}
                    >
                      {incident?.time}
                    </span>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      </div>
      {/* Zoom Controls */}
      <div className="absolute bottom-4 left-4 z-10 flex flex-col gap-2">
        <button
          className="w-10 h-10 flex items-center justify-center bg-card border-2 border-border hover:border-primary rounded-[var(--radius-sm)] transition-all"
          style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.15)' }}
          aria-label="Zoom inn"
        >
          <Icon name="Plus" size={18} color="var(--color-foreground)" />
        </button>
        <button
          className="w-10 h-10 flex items-center justify-center bg-card border-2 border-border hover:border-primary rounded-[var(--radius-sm)] transition-all"
          style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.15)' }}
          aria-label="Zoom ut"
        >
          <Icon name="Minus" size={18} color="var(--color-foreground)" />
        </button>
      </div>
    </div>
  );
};

export default TrafficMap;